import SubHeader from "../../components/header/SubHeader";
import Button from "../../components/common/button/Button";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {fetchDuePayments, updateVendorPartialPayment, updateVendorPayment} from "../../store/vendor-slice";
import Card from "../../components/ui/card/Card";
import {camelToTitle, reverseEnum} from "../../components/Utility";
import {PaymentStatus, PaymentType} from "../../enums/enums";
import {fetchVendorList} from "../../store/list-slice";

const VendorDues = () => {
    const navigator = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const [isPartial, setIsPartial] = useState(false);
    const list = useSelector(state => state.list);
    const {items: vendors} = list.vendor;

    const userRole = useSelector((state) => state.auth.selectedRole);
    const {duePayment, fetchDuePaymentStatus, updateVendorPaymentStatus, error} = useSelector((state) => state.vendor);

    const [formData, setFormData] = useState({
        orderIds: [],
        amount: '',
        paymentMethod: PaymentType.Cash
    });

    //Basic Form error
    const [errors, setErrors] = useState({
        orderIds: '',
        amount: '',
    });

    const handleFormChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateField = (id, value) => {
        let error = "";
        const trimmed = typeof value === "string" ? value.trim() : value;
        switch (id) {
            case "orderIds":
                if ((!Array.isArray(value) || value.length === 0) && (!isPartial)) {
                    error = "Select at least one order.";
                }
                break;

            case "amount":
                if (!trimmed && isPartial) {
                    error = "Amount is required";
                }
                break;

            default:
                break;
        }

        setErrors((prev) => ({...prev, [id]: error}));
        return error === "";
    };

    const handleBlur = (e) => {
        const {id, value} = e.target;
        validateField(id, value);
    };

    const validateForm = () => {
        let isValid = true;
        Object.entries(formData).forEach(([key, value]) => {
            const valid = validateField(key, value);
            if (!valid) isValid = false;
        });
        return isValid;
    };

    const handleCheck = (orderId) => {
        setFormData((prev) => {
            const isSelected = prev.orderIds.includes(orderId);
            return {
                ...prev,
                orderIds: isSelected
                    ? prev.orderIds.filter((id) => id !== orderId) // remove
                    : [...prev.orderIds, orderId],                // add
            };
        });
    };

    const selectedDue = (duePayment?.purchaseOrders || [])
        .filter((order) => formData.orderIds.includes(order._id))
        .reduce((sum, order) => sum + (parseFloat(order.dueAmount) || 0), 0);

    const clearDuesHandler = () => {
        const isFormValid = validateForm();
        if (!isFormValid) return;

        const payload = isPartial
            ? {
                amount: formData.amount,
                paymentMethod: formData.paymentMethod,
            }
            : {
                orderIds: formData.orderIds,
                amount: selectedDue,
                paymentMethod: formData.paymentMethod,
            };

        if (isPartial) {
            dispatch(updateVendorPartialPayment({...userRole, data: payload, id: params.vendorId}));
        } else {
            dispatch(updateVendorPayment({...userRole, data: payload, id: params.vendorId}));
        }
    }

    const getVendorName = (vendors, vendorId) => {
        return vendors[vendorId] || "Unknown Vendor";
    }

    useEffect(() => {
        if (updateVendorPaymentStatus === 'success') {
            navigator(0);
        }
    }, [updateVendorPaymentStatus, navigator])

    useEffect(() => {
        dispatch(fetchDuePayments({...userRole, id: params.vendorId}))
        dispatch(fetchVendorList({...userRole}))
    }, [dispatch, userRole, params.vendorId])

    return (
        <>
        <SubHeader
            title={`Vendor Dues - (${getVendorName(vendors, params.vendorId)})`}>
                <Button to={`/vendor/${params.vendorId}/history`}
                        className="btn-sm bg-primary bg-opacity-50">History</Button>
                <Button to={`/vendor/${params.vendorId}/purchase`}
                        className="btn-sm bg-primary bg-opacity-50">Purchase</Button>
                {/*<Button to="../../" className="btn-sm bg-secondary bg-opacity-50">Back</Button>*/}
            </SubHeader>
            <div className="container p-0 ms-0">
                {fetchDuePaymentStatus === "loading" && <p>Loading...</p>}
                {fetchDuePaymentStatus === "failed" && <h6 className="text-danger">{error}</h6>}
                {duePayment?.purchaseOrders?.length === 0 && (
                    <h6 className="text-danger ss">No due payments found for this vendor.</h6>
                )}
                {fetchDuePaymentStatus === "success" &&
                    <>
                        {/*Basic details to show*/}
                        <div className="row">
                            <div className="col-md-3">
                                <Card>
                                    <label className="form-label fs-6 opacity-75">Total Amount</label>
                                    <h6 className="fs-5">₹ {duePayment.totalAmount}/-</h6>
                                </Card>
                            </div>
                            <div className="col-md-3">
                                <Card className="border border-warning">
                                    <label className="form-label fs-6 opacity-75">Total Paid Payment</label>
                                    <h6 className="fs-5">₹ {duePayment.totalPaidAmount}/-</h6>
                                </Card>
                            </div>
                            <div className="col-md-3">
                                <Card className="border border-danger">
                                    <label className="form-label opacity-75 fs-6">Due Amount</label>
                                    <h6 className="fs-5">₹ {duePayment.totalDueAmount}/-</h6>
                                </Card>
                            </div>
                            <div className="col-md-3">
                                <Card className="border border-info">
                                    <label className="form-label fs-6 opacity-75">Advanced Amount</label>
                                    <h6 className="fs-5">₹ {duePayment.advancedAmount}/-</h6>
                                </Card>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mt-6 mb-4">Purchase Orders</h5>
                            <div className="form-check form-switch">
                                <label className="form-check-label" htmlFor="isPartial">Is Partial</label>
                                <input className="form-check-input"
                                       name="isPartial"
                                       type="checkbox"
                                       role="switch"
                                       id="isPartial"
                                       onChange={(e) => {
                                           setIsPartial(e.target.checked);
                                           setErrors({
                                               orderIds: '',
                                               amount: '',
                                           });
                                       }}
                                />
                            </div>
                        </div>
                        <div className="d-flex justify-content-end align-items-center mb-4">
                            <select
                                className="form-select form-select-sm me-3"
                                style={{maxWidth: 250}}
                                name="paymentMethod"
                                value={formData.paymentMethod}
                                onChange={(e) => handleFormChange("paymentMethod", e.target.value)}
                            >
                                <option value="" disabled>--Select payment method</option>
                                {
                                    Object.keys(PaymentType).map((key) => {
                                        const value = PaymentType[key];
                                        return <option key={key} value={value}>{camelToTitle(key)}</option>
                                    })
                                }
                            </select>

                            {/*Handler for partial payments*/}
                            {isPartial && <div className="d-flex gap-3 align-items-center">
                                <input
                                    className="form-control fs-md"
                                    style={{height: 32}}
                                    id="amount"
                                    name="amount"
                                    placeholder="Enter partial amount"
                                    type="text"
                                    onBlur={handleBlur}
                                    value={formData.amount}
                                    onChange={(e) => handleFormChange("amount", e.target.value)}
                                />
                                <Button
                                    className="bg-success text-white w-50 text-nowrap"
                                    onClick={clearDuesHandler}
                                    disabled={updateVendorPaymentStatus === 'loading'}
                                >Pay ₹{formData.amount}/-</Button>
                            </div>}

                            {/*Handler for non-partial payment*/}
                            {(!isPartial) && <Button
                                className="bg-success text-white"
                                onClick={clearDuesHandler}
                                disabled={updateVendorPaymentStatus === 'loading'}
                            >Pay ₹{selectedDue}/-</Button>}
                        </div>
                        {/*Display error if not id is selected*/}
                        {errors.orderIds &&
                            <div className="invalid-feedback fs-md d-block">{errors.orderIds}</div>}
                        {errors.amount &&
                            <div className="invalid-feedback fs-md d-block">{errors.amount}</div>}

                        {/*Table to manually select orders*/}
                        {(!isPartial) && <table className="table table-sm fs-md mobile-responsive">
                            <thead>
                            <tr>
                                <th></th>
                                <th>Total Amount</th>
                                <th>Initial Paid Amount</th>
                                <th>Settlement Revision</th>
                                <th>Due Amount</th>
                                <th>Purchase Method</th>
                                <th>Payment Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {duePayment?.purchaseOrders?.map((order, index) => (
                                <tr key={index}>
                                    <td>
                                        <input type="checkbox"
                                               className="form-check-input"
                                               checked={formData.orderIds.includes(order._id)}
                                               onChange={() => handleCheck(order._id)}
                                        />
                                    </td>
                                    <td>{order.totalAmount}</td>
                                    <td>{order.paidAmount}</td>
                                    <td>{order.totalPayment}</td>
                                    <td>{order.dueAmount}</td>
                                    <td>{order.paymentMethod}</td>
                                    <td>{reverseEnum(PaymentStatus)[order.paymentStatus]}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>}
                    </>
                }
            </div>
        </>
    )
}

export default VendorDues;